
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function BigBayPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Big Bay Painters Cape Town | Professional Painting Services | Coastal Property Specialists";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Big Bay properties. Expert coastal painting, marine-grade protection, interior & exterior painting. Serving Big Bay Cape Town with premium quality finishes.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Big Bay painters, coastal painting Cape Town, marine grade painting, Big Bay property painting, professional painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/big-bay`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Big Bay Painters - Professional Painting Services Cape Town",
      "description": "Professional painting services for Big Bay properties with coastal protection and premium finishes.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/big-bay`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Big Bay",
        "description": "Specialized painting services for Big Bay's coastal properties with marine-grade protection.",
        "areaServed": "Big Bay, Cape Town",
        "serviceType": "Coastal Property Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Big Bay')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Residential Painting',
      description: 'Complete interior and exterior painting for Big Bay homes and apartments with weather-resistant finishes.',
      price: 'From R8,000',
      features: ['Interior painting', 'Exterior coating', 'Color consultation', 'Surface preparation']
    },
    {
      icon: 'ri-building-line',
      title: 'Apartment Complex Painting',
      description: 'Large-scale painting projects for Big Bay residential complexes and developments with coordinated scheduling.',
      price: 'From R25,000',
      features: ['Multi-unit coordination', 'Bulk pricing', 'Phased completion', 'Minimal disruption']
    },
    {
      icon: 'ri-brush-line',
      title: 'Coastal Maintenance',
      description: "Regular maintenance painting to protect against Big Bay's coastal weather conditions and salt air exposure.",
      price: 'From R5,000',
      features: ['Scheduled maintenance', 'Touch-up services', 'Protective treatments', 'Inspection reports']
    },
    {
      icon: 'ri-shield-line',
      title: 'Waterproofing Solutions',
      description: 'Comprehensive waterproofing services essential for Big Bay properties exposed to coastal moisture and weather.',
      price: 'From R7,500',
      features: ['Membrane application', 'Crack repair', 'Moisture barriers', 'Warranty coverage']
    },
    {
      icon: 'ri-palette-line',
      title: 'Color Design Services',
      description: 'Professional color consultation and design services tailored to Big Bay\'s coastal aesthetic and lifestyle.',
      price: 'From R2,000',
      features: ['Color matching', 'Trend analysis', 'Sample testing', 'Design coordination']
    },
    {
      icon: 'ri-tools-line',
      title: 'Restoration Services',
      description: 'Specialized restoration painting for older Big Bay properties requiring careful attention to original character.',
      price: 'From R10,000',
      features: ['Heritage restoration', 'Surface repair', 'Matching techniques', 'Preservation methods']
    }
  ];

  const testimonials = [
    {
      name: 'Mark Stevens',
      property: 'Seaside Apartment',
      rating: 5,
      text: 'Great service for our Big Bay apartment. The team was efficient and the results are excellent. They handled the coastal conditions perfectly and the paint has lasted beautifully.',
      image: 'https://public.readdy.ai/ai/img_res/76173c845a14264ca71caa69600751b5.jpg'
    },
    {
      name: 'Rachel Cooper',
      property: 'Family Townhouse',
      rating: 5,
      text: 'Fantastic work on our townhouse exterior. They handled the coastal conditions perfectly and provided excellent advice on color selection for our beachside location.',
      image: 'https://readdy.ai/api/search-image?query=Happy%20woman%20homeowner%20standing%20outside%20Big%20Bay%20townhouse%20with%20Big%20Bay%20beach%20in%20background%2C%20freshly%20painted%20exterior%2C%20coastal%20residential%20area%2C%20satisfied%20client%20portrait&width=80&height=80&seq=bigbay2new&orientation=squarish'
    },
    {
      name: 'James Wilson',
      property: 'Beachfront Home',
      rating: 5,
      text: 'Outstanding professional service from start to finish. The marine-grade coating has protected our property excellently against the salt air and coastal weather.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20man%20homeowner%20at%20Big%20Bay%20beachfront%20home%20with%20Robben%20Island%20view%2C%20newly%20painted%20exterior%2C%20ocean%20proximity%2C%20satisfied%20customer%20portrait&width=80&height=80&seq=bigbay5new&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Coastal Apartment Complex',
      description: 'Full exterior renovation with weather-resistant coatings and coordinated color scheme across multiple buildings',
      image: 'https://readdy.ai/api/search-image?query=Modern%20apartment%20complex%20in%20Big%20Bay%20with%20Robben%20Island%20view%2C%20freshly%20painted%20exterior%2C%20coastal%20architecture%2C%20professional%20painting%20work%2C%20Big%20Bay%20beach%20proximity&width=400&height=300&seq=bigbay3new&orientation=landscape'
    },
    {
      title: 'Beachside Townhouses',
      description: 'Complete interior and exterior painting project with specialized coastal protection and modern color palette',
      image: 'https://readdy.ai/api/search-image?query=Row%20of%20townhouses%20in%20Big%20Bay%20with%20Big%20Bay%20beach%20view%2C%20newly%20painted%20exteriors%2C%20coastal%20residential%20development%2C%20modern%20architecture%2C%20professional%20painting%20finish&width=400&height=300&seq=bigbay4new&orientation=landscape'
    },
    {
      title: 'Family Home Renovation',
      description: 'Comprehensive painting and waterproofing project including decorative finishes and protective coatings',
      image: 'https://readdy.ai/api/search-image?query=Big%20Bay%20family%20home%20with%20Robben%20Island%20backdrop%2C%20professional%20exterior%20painting%2C%20coastal%20residential%20property%2C%20modern%20renovation%2C%20quality%20finish%20work&width=400&height=300&seq=bigbay6new&orientation=landscape'
    },
    {
      title: 'Holiday Rental Properties',
      description: 'Multi-property maintenance program for Big Bay holiday rentals with durable finishes and regular upkeep',
      image: 'https://readdy.ai/api/search-image?query=Big%20Bay%20holiday%20rental%20properties%20with%20Big%20Bay%20beach%20setting%2C%20freshly%20maintained%20painted%20exteriors%2C%20coastal%20vacation%20homes%2C%20professional%20maintenance%20work&width=400&height=300&seq=bigbay7new&orientation=landscape'
    }
  ];

  const paintingProcess = [
    {
      step: '1',
      title: 'Site Assessment',
      description: 'Thorough evaluation of your Big Bay property including coastal exposure analysis and surface condition assessment.'
    },
    {
      step: '2',
      title: 'Custom Solution',
      description: 'Development of tailored painting solutions considering Big Bay\'s coastal environment and your specific requirements.'
    },
    {
      step: '3',
      title: 'Preparation Work',
      description: 'Comprehensive surface preparation including cleaning, repairs, and priming to ensure optimal paint adhesion.'
    },
    {
      step: '4',
      title: 'Professional Application',
      description: 'Expert application of weather-resistant paints using proven techniques for superior coastal protection.'
    },
    {
      step: '5',
      title: 'Quality Control',
      description: 'Detailed inspection and final touch-ups to ensure every aspect meets our high standards.'
    },
    {
      step: '6',
      title: 'Maintenance Guidance',
      description: 'Comprehensive care instructions and maintenance recommendations for your Big Bay coastal property.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Big%20Bay%20residential%20property%20with%20Robben%20Island%20view%2C%20modern%20apartment%20complexes%2C%20painting%20equipment%2C%20scaffolding%2C%20beachside%20homes%2C%20sunny%20day&width=1920&height=1080&seq=bigbayhero2&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Big Bay
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Big Bay homes and apartments. Coastal-resistant finishes and reliable service for your beachside property with specialized protection against the marine environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Big Bay Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Expert Painting Services in Big Bay
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Big Bay is one of Cape Town's most popular coastal residential areas, known for its beautiful beaches, modern developments, and relaxed seaside lifestyle. This vibrant community attracts families, young professionals, and holiday home owners who appreciate the perfect balance of coastal living and urban convenience.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The coastal location of Big Bay presents unique challenges for property maintenance, particularly when it comes to painting and protective coatings. The combination of salt air, strong winds, and intense UV radiation requires specialized painting solutions that go beyond standard residential treatments.
              </p>
              <p className="text-lg text-gray-600">
                Our team has extensive experience working in Big Bay's diverse property types, from modern apartment complexes to family townhouses and beachfront homes. We understand the specific requirements of each property type and provide tailored solutions that ensure long-lasting protection and aesthetic appeal.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Big%20Bay%20coastal%20residential%20area%20with%20modern%20homes%20and%20apartments%2C%20Robben%20Island%20visible%20in%20distance%2C%20beachside%20location%2C%20contemporary%20architecture%2C%20Big%20Bay%20beach&width=600&height=400&seq=bigbay8new&orientation=landscape"
                alt="Big Bay Residential Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Big Bay Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional painting solutions designed specifically for Big Bay's residential properties and coastal developments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Big Bay Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results for your Big Bay coastal property.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Big Bay */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Big Bay?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in Big Bay's unique coastal painting requirements with proven results and satisfied customers.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Local Knowledge</h3>
              <p className="text-gray-600">Extensive experience with Big Bay's coastal conditions, residential properties, and community requirements gained through years of dedicated service.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Weather Resistant</h3>
              <p className="text-gray-600">Specialized coatings and paints designed specifically for Big Bay's coastal environment, providing superior protection against salt air and moisture.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Competitive Pricing</h3>
              <p className="text-gray-600">Fair and transparent pricing for all Big Bay painting projects, large and small, with detailed quotes and no hidden costs.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Experienced Team</h3>
              <p className="text-gray-600">Skilled painters with years of experience in Big Bay residential projects, trained in coastal painting techniques and customer service excellence.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coastal Challenges Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Big Bay Coastal Painting Challenges
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Environmental Factors We Address</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-drop-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Salt Air Exposure</h4>
                    <p className="text-gray-600">Big Bay's proximity to the ocean means constant exposure to salt-laden air that can cause rapid deterioration of standard paint finishes.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-windy-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Coastal Winds</h4>
                    <p className="text-gray-600">Strong coastal winds carry sand and salt particles that can abrade paint surfaces, requiring enhanced durability solutions.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-sun-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">UV Radiation</h4>
                    <p className="text-gray-600">Intense coastal sunlight requires UV-resistant formulations to prevent color fading and surface chalking over time.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-water-percent-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Humidity Control</h4>
                    <p className="text-gray-600">High coastal humidity levels can promote mold and mildew growth, requiring specialized moisture-resistant treatments.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Big%20Bay%20coastal%20environment%20with%20Robben%20Island%20view%2C%20ocean%20spray%2C%20salt%20air%20effects%2C%20coastal%20winds%2C%20UV%20exposure%20on%20residential%20properties%2C%20environmental%20challenges&width=600&height=500&seq=bigbay9new&orientation=portrait"
                alt="Big Bay Coastal Environment"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Big Bay Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in Big Bay residential painting with recent successful projects across the area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Big Bay Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Big Bay property owners who have experienced our professional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Big Bay Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's trusted painting professionals for Big Bay. Our experienced team is ready to provide you with a comprehensive consultation and detailed estimate for your coastal property. Experience the difference that specialized coastal painting expertise makes for your Big Bay home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
